import type {RenderingStrategy} from '../../schemas/contentful/contentTypes/renderingStrategy'
import type {PrimerComponentHero} from '../../schemas/contentful/contentTypes/primerComponentHero'

export type UserState = {
  isLoggedIn?: boolean
  isTeamAdmin?: boolean
  isEnterpriseAdmin?: boolean
  hasCopilotEdu?: boolean
  hasCopilotFree?: boolean
  hasCopilotPro?: boolean
  hasCopilotProPlus?: boolean
  hasCopilotMax?: boolean
  hasCopilotBusiness?: boolean
  hasCopilotEnterprise?: boolean
  isTeamMember?: boolean
  isEnterpriseMember?: boolean
}

export type RenderingStrategyResult = {
  component: PrimerComponentHero
  strategy: string
  strategyTitle?: string
}

/**
 * Evaluates rendering strategies using match-first pattern.
 * Returns the component and strategy metadata from the first strategy whose condition matches,
 * or null if no strategies match.
 *
 * @param strategies - Array of rendering strategies from Contentful
 * @param userState - Current user state (e.g., { isLoggedIn: true })
 * @returns Object with component and strategy metadata, or null if no match
 *
 * @example
 * ```tsx
 * const result = evaluateRenderingStrategy(heroStrategies, { isLoggedIn: true })
 * if (result) {
 *   // result.component - the hero to render
 *   // result.strategy - e.g., "LOGGED_IN" for analytics
 *   // result.strategyTitle - e.g., "Logged In Users" for debugging
 * }
 * ```
 */
export function evaluateRenderingStrategy(
  strategies: RenderingStrategy[] | undefined,
  userState: UserState,
): RenderingStrategyResult | null {
  if (!strategies || strategies.length === 0) {
    return null
  }

  for (const strategy of strategies) {
    const {strategy: condition, title} = strategy.fields

    switch (condition) {
      case 'LOGGED_IN':
        if (userState.isLoggedIn === true) {
          return {
            component: strategy.fields.component,
            strategy: condition,
            strategyTitle: title,
          }
        }
        break
    }
  }

  // No matching strategy found
  return null
}
